// user/components/ProvidersList.js

import React, { useState, useEffect } from "react";
import ProviderForm from "./ProviderForm";
import ModalOverlay from "../../components/ModalOverlay";
import {
  invitarProveedor,
  actualizarProveedor,
  reInvitarProveedor,
  obtenerProveedoresConToken
} from '../../services/api/modules/providers';

import { toast } from 'react-toastify';
import { API_URL } from '../../services/api/config';

export default function ProvidersList({ ownerId, onClose }) {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  useEffect(() => {
    cargarProveedores();
  }, [ownerId]);

  async function cargarProveedores() {
    try {
      setLoading(true);
      
      // Obtener el token disponible
      const urlParams = new URLSearchParams(window.location.search);
      const token = localStorage.getItem('nfcToken') || urlParams.get('token');
      
      // Si tenemos token, guardarlo temporalmente
      if (token) {
        localStorage.setItem('nfcToken', token);
      }
      
      // Usar la función importada del módulo providers
      const result = await obtenerProveedoresConToken(ownerId, token);
       
      if (result.success) {
        setProveedores(result.data || []);
      } else if (!result.error) {
        setProveedores([]);
      } else {
        toast.error("Error cargando proveedores");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  }

  function handleAddProvider() {
    setSelectedProvider(null);
    setShowForm(true);
  }

  function handleEditProvider(provider) {
    setSelectedProvider(provider);
    setShowForm(true);
  }

  async function handleSave(formData) {
    try {
      let result;
      
      if (selectedProvider?.id) {
        // Actualizar proveedor existente
        result = await actualizarProveedor(selectedProvider.id, formData);
      } else {
        // Crear nuevo proveedor
        result = await invitarProveedor(formData);
      }

      // Manejar respuesta según las nuevas reglas
      if (result.error) {
        // CASO ESPECIAL: Proveedor con invitación pendiente
        if (result.estado === "pendiente") {
          toast.warning(result.mensaje, {
            autoClose: 8000, // Más tiempo para leer
            position: "top-center"
          });
          // Opcional: Mostrar modal con instrucciones
          if (window.confirm(result.mensaje + "\n\n¿Deseas ver los proveedores actuales?")) {
            setShowForm(false);
          }
        } else {
          // Otros errores
          toast.error(result.mensaje || result.error || "Error al guardar");
        }
        return;
      }

      // Operación exitosa
      if (result.success || result.data) {
        const data = result.data || result;
        const mensaje = data.mensaje || "";
        
        // Mensajes específicos según el caso
        if (mensaje.includes("ya está en tu lista")) {
          toast.info(mensaje);
        } else if (mensaje.includes("agregado exitosamente")) {
          toast.success(mensaje);
        } else if (mensaje.includes("Invitación enviada")) {
          toast.success(mensaje);
        } else if (!formData.correo) {
          toast.success("Proveedor agregado localmente (sin email)");
        } else {
          toast.success("Operación completada exitosamente");
        }
        
        await cargarProveedores();
        setShowForm(false);
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      toast.error("Error al procesar la solicitud");
    }
  }

  async function handleReInvite(provider) {
    // Validación: no re-invitar si está verificado
    if (provider.estado === "verificado") {
      toast.info("Este proveedor ya está verificado");
      return;
    }
    
    if (provider.estado === "rechazado") {
      if (!window.confirm(`${provider.nombre} rechazó la invitación anterior. ¿Deseas enviar una nueva invitación?`)) {
        return;
      }
    }
    
    try {
      const result = await reInvitarProveedor(provider.id);
      if (result.success || result.data) {
        toast.success(`Re-invitación enviada a ${provider.correo}`);
        await cargarProveedores();
      } else {
        toast.error(result.error || result.mensaje || "Error al re-invitar");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al re-invitar");
    }
    setShowForm(false);
  }

  const handleDeleteProvider = async (proveedor) => {
    // Mensaje contextual según el estado
    let confirmMessage = `¿Estás seguro de eliminar a ${proveedor.nombre} de tu lista?`;
    
    if (proveedor.estado === "verificado") {
      confirmMessage += "\n\nEl proveedor ya no recibirá recordatorios sobre tus mascotas.";
    } else if (proveedor.estado === "pendiente") {
      confirmMessage += "\n\nLa invitación pendiente quedará sin efecto.";
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = localStorage.getItem('nfcToken') || urlParams.get('token');
      
      const response = await fetch(`${API_URL}/api/proveedores/${proveedor.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || 'Proveedor eliminado de tu lista');
        cargarProveedores();
      } else {
        toast.error(result.error || 'Error al eliminar proveedor');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  function getBadgeClass(estado) {
    switch(estado) {
      case "verificado": return "badge bg-success";
      case "pendiente": return "badge bg-warning text-dark";
      case "rechazado": return "badge bg-danger";
      case "sinCorreo": return "badge bg-secondary";
      default: return "badge bg-light text-dark";
    }
  }

  function getEstadoText(estado) {
    switch(estado) {
      case "verificado": return "Verificado ✓";
      case "pendiente": return "Invitación Pendiente";
      case "rechazado": return "Rechazado";
      case "sinCorreo": return "Sin Email";
      default: return estado;
    }
  }

  function getTipoServicioIcon(tipo) {
    switch(tipo) {
      case "paseador": return "🚶";
      case "bañador": return "🛁";
      case "cuidador": return "🏠";
      case "transporte": return "🚗";
      case "alimentacion": return "🍖";
      default: return "📋";
    }
  }

  return (
    <div className="container" 
         style={{ maxWidth: "800px", margin: "auto" }}>
      {/* Header con gradiente feature */}
      <div className="bg-feature-gradient text-white p-4 position-relative"
           style={{
             borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
             padding: 'var(--space-5)'
           }}>
        <h4 className="mb-0"
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 'var(--font-weight-bold)'
            }}>
          <i className="bi bi-people-fill me-2"></i>
          Gestión de Proveedores de Servicios
        </h4>
        <p className="mb-0 mt-2 opacity-75"
           style={{fontSize: 'var(--font-size-sm)'}}>
          Administra los proveedores que brindan servicios a tus mascotas
        </p>
        {onClose && (
          <button
            type="button"
            className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
            onClick={onClose}
          />
        )}
      </div>

      {/* Body */}
      <div className="bg-white border border-top-0 p-4"
           style={{
             borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
             padding: 'var(--space-5)',
             borderColor: 'var(--gray-300)'
           }}>
        <button 
          className="btn btn-feature mb-3 transition-base"
          onClick={handleAddProvider}
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            padding: 'var(--space-3) var(--space-4)'
          }}
        >
          <i className="bi bi-person-plus me-2"></i>
          Agregar Proveedor
        </button>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : proveedores.length === 0 ? (
          <div className="alert alert-info rounded-lg"
               style={{
                 borderRadius: 'var(--radius-md)',
                 padding: 'var(--space-4)',
                 fontSize: 'var(--font-size-sm)'
               }}>
            <i className="bi bi-info-circle me-2"></i>
            No tienes proveedores registrados. Agrega uno para comenzar.
          </div>
        ) : (
          <div className="row g-3" 
               style={{gap: 'var(--space-3)'}}>
            {proveedores.map(provider => (
              <div className="col-md-6" key={provider.id}>
                <div className="card h-100 card-elevated"
                     style={{borderRadius: 'var(--radius-lg)'}}>
                  <div className="card-header d-flex justify-content-between align-items-center"
                       style={{
                         padding: 'var(--space-3) var(--space-4)',
                         backgroundColor: 'var(--gray-50)',
                         borderBottom: `1px solid var(--gray-300)`
                       }}>
                    <div style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-semibold)'
                    }}>
                      <span className="me-2">{getTipoServicioIcon(provider.tipoServicio)}</span>
                      <strong>{provider.nombre}</strong>
                    </div>
                    <span className={getBadgeClass(provider.estado)}
                          style={{fontSize: 'var(--font-size-xs)'}}>
                      {getEstadoText(provider.estado)}
                    </span>
                  </div>
                  
                  <div className="card-body"
                       style={{padding: 'var(--space-4)'}}>
                    {provider.correo && (
                      <p className="mb-2"
                         style={{fontSize: 'var(--font-size-sm)'}}>
                        <i className="bi bi-envelope me-2 text-muted"></i>
                        {provider.correo}
                      </p>
                    )}
                    
                    {provider.telefono && (
                      <p className="mb-2"
                         style={{fontSize: 'var(--font-size-sm)'}}>
                        <i className="bi bi-telephone me-2 text-muted"></i>
                        <a href={`tel:${provider.telefono}`}>{provider.telefono}</a>
                      </p>
                    )}
                    
                    <p className="mb-0"
                       style={{fontSize: 'var(--font-size-sm)'}}>
                      <i className="bi bi-briefcase me-2 text-muted"></i>
                      <span className="text-capitalize">{provider.tipoServicio}</span>
                    </p>

                    {/* Información adicional según estado */}
                    {provider.estado === "pendiente" && provider.tokenExpira && (
                      <p className="mb-0 mt-2 text-warning"
                         style={{fontSize: 'var(--font-size-xs)'}}>
                        <i className="bi bi-clock me-1"></i>
                        Invitación expira pronto
                      </p>
                    )}
                  </div>
                  
                  <div className="card-footer bg-light"
                       style={{
                         padding: 'var(--space-3) var(--space-4)',
                         backgroundColor: 'var(--gray-50)',
                         borderTop: `1px solid var(--gray-300)`
                       }}>
                    <div className="d-flex gap-2 flex-wrap"
                         style={{gap: 'var(--space-2)'}}>
                      {provider.estado !== "verificado" && (
                        <button
                          className="btn btn-sm btn-outline-secondary transition-base"
                          onClick={() => handleEditProvider(provider)}
                          style={{fontSize: 'var(--font-size-xs)'}}>
                          <i className="bi bi-pencil me-1"></i>
                          Editar
                        </button>
                      )}
                      
                      {(provider.estado === "pendiente" || provider.estado === "rechazado") && provider.correo && (
                        <button
                          className="btn btn-sm btn-outline-warning transition-base"
                          onClick={() => handleReInvite(provider)}
                          style={{fontSize: 'var(--font-size-xs)'}}>
                          <i className="bi bi-arrow-clockwise me-1"></i>
                          Re-invitar
                        </button>
                      )}
                      
                      <button
                        className="btn btn-sm btn-outline-danger transition-base"
                        onClick={() => handleDeleteProvider(provider)}
                        title="Eliminar de mi lista"
                        style={{fontSize: 'var(--font-size-xs)'}}>
                        <i className="bi bi-trash me-1"></i>
                        Eliminar
                      </button>
                      
                      {provider.estado === "verificado" && (
                        <span className="text-success d-flex align-items-center ms-auto"
                              style={{fontSize: 'var(--font-size-xs)'}}>
                          <i className="bi bi-check-circle me-1"></i>
                          Activo
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para crear/editar */}
      {showForm && (
        <ModalOverlay
          onClose={() => setShowForm(false)}
          containerStyle={{
            padding: "0",
            maxWidth: "500px",
            width: "90%",
            borderRadius: 'var(--radius-xl)'
          }}
        >
          <ProviderForm
            providerData={selectedProvider}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            onReInvite={handleReInvite}
          />
        </ModalOverlay>
      )}
    </div>
  );
}